import { toast } from 'react-toastify';
import DashboardViewControls from '@/app/dashboard/components/dashboard-view-controls';
import DashboardEditControls from '@/app/dashboard/components/dashboard-edit-controls';
import { DashboardAction, DashboardState } from '../../store/dashboard-content-reducer';
import { MNIDashboard } from '@/core/preferences/export';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DashboardUserMenu from '../dashboard-user-menu';
import { NotificationBell } from '@/core/notifications/export';
import { faGlobe, faTableColumns } from '@fortawesome/free-solid-svg-icons';
import { shareApi } from '@/shared/share/export';
import { ActiveWidgetsForDashboard } from '../../hooks/use-widgets-from-layouts';
import { authService, useSessionId } from '@/core/auth/export';
import { analyticsService, AnalyticsEvent, AnalyticsTypes } from '@/shared/analytics/export';
import { Search } from '@/core/search/export';
import { getBrandLogo, isAlphaFlashHost } from '@/shared/theme/export';
import { Permissions, RequirePermissions } from '@/core/permissions/export';
import { TestEnvIndicator } from '@/shared/test-env-indicator/export';
import '@/shared/theme/navbar-buttons.scss';

export default function Navbar({
  state,
  selectedDashboard,
  notificationSoundOn,
  activeWidgets,
  setNotificationSoundOn,
  setShowCreateDashboardModal,
  setShowDeleteDashboardModal,
  setShowMarkAsFavoriteDashboardModal,
  dispatch,
  addWidget,
}: {
  state: DashboardState;
  selectedDashboard?: MNIDashboard;
  notificationSoundOn: boolean;
  activeWidgets?: ActiveWidgetsForDashboard;
  setNotificationSoundOn: (on: boolean) => void;
  setShowCreateDashboardModal: (visible: boolean) => void;
  setShowDeleteDashboardModal: (visible: boolean) => void;
  setShowMarkAsFavoriteDashboardModal: (visible: boolean) => void;
  dispatch: (a: DashboardAction) => void;
  addWidget: (widgetId: string) => void;
}) {
  const onDashboardNameChange = (s: string) => {
    dispatch({
      type: 'setCurrentDashboardName',
      name: s,
    });
  };

  const sessionId = useSessionId() ?? '';
  const subject = authService.getSubjectData();

  const onDashboardSelect = (value: MNIDashboard) => {
    dispatch({
      type: 'selectDashboard',
      dashboard: value,
      sessionId,
    });
    setShowCreateDashboardModal(false);
  };

  const onDashboardShare = async (value: MNIDashboard) => {
    const copyValue = {
      name: value.name,
      layouts: value.layouts,
      widgetPreferences: value.widgetPreferences,
    };

    const id = await shareApi.getShareLink(copyValue);

    const url = new URL(window.location.href);
    url.searchParams.set('share', id || '');
    const shareString = url.toString();

    await navigator.clipboard.writeText(shareString);

    analyticsService.recordAnalyticsEvent(AnalyticsEvent.DASHBOARD_SHARED, {
      subject,
      object: {
        type: AnalyticsTypes.DASHBOARD,
        id: value.id,
        title: value.name,
      },
      session: {
        id: sessionId,
      },
    });

    return toast.info('Text copied into clipboard');
  };

  return (
    <nav className="navbar navbar-expand-sm navbar-dark sticky-top">
      <div className="container-fluid px-4">
        {/*  eslint-disable-next-line  */}
        <a className="navbar-brand" href="#">
          <img alt="logo" src={getBrandLogo()} height={'24'} />
        </a>
        {/* Always present (even when the badge itself renders nothing) so this
            flex item keeps absorbing the free space between the logo and the
            search/selector block — without it, that space isn't claimed by
            anyone and shows up as a gap before the right-side icons instead. */}
        <div style={{ flexGrow: 1, minWidth: 0, display: 'flex' }}>
          <TestEnvIndicator />
        </div>
        {/* Always visible — on mobile sits between logo and toggler; on desktop ordered last */}
        <div className="d-flex align-items-center ms-auto me-1 ms-sm-0 me-sm-0 order-sm-last">
          {!state.loading && state.selectedDashboard && !state.editing && !isAlphaFlashHost() && (
            <button
              className={'btn btn-outline-primary-500 btn-sm navbar-btn navbar-btn-square ms-3'}
              style={{ fontFamily: 'Inter' }}
              onClick={() => window.open('https://mnimarkets.com', 'mni_website')}
            >
              <FontAwesomeIcon icon={faGlobe} style={{ fontSize: '1.15em' }} />
            </button>
          )}

          {process.env.REACT_APP_TEST_ENV === 'true' ? (
            <NotificationBell />
          ) : (
            <RequirePermissions allow={Permissions.NOTIFICATIONS}>
              <NotificationBell />
            </RequirePermissions>
          )}

          {/* User menu: always visible on desktop; on mobile accessible via hamburger (d-none d-sm-flex) */}
          <span className="d-none d-sm-flex">
            <DashboardUserMenu
              className={'ms-3'}
              notificationSoundOn={notificationSoundOn}
              setNotificationSoundOn={setNotificationSoundOn}
            />
          </span>
        </div>

        <button
          className="navbar-toggler"
          type="button"
          style={{ height: '40px', fontFamily: 'Inter' }}
          data-bs-toggle="collapse"
          data-bs-target="#navbarSupportedContent"
          aria-controls="navbarSupportedContent"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        <div
          className="collapse navbar-collapse"
          id="navbarSupportedContent"
          style={{ flexGrow: 0 }}
        >
          <ul className="navbar-nav ms-auto mb-2 mb-lg-0">
            {!state.loading && (
              <div className={'d-flex '}>
                <div className={'d-flex'}>
                  <Search />
                  {state.selectedDashboard ? (
                    state.editing ? (
                      // Stack the (hidden) view controls behind the edit controls so this
                      // wrapper's width is never narrower than view mode's — without that,
                      // switching into edit mode could visibly shrink/reflow the toolbar.
                      // Only done while editing: view mode alone (the common case) stays at
                      // its own natural, tighter width instead of always matching edit mode's.
                      <div className={'d-grid'}>
                        <div style={{ gridArea: '1 / 1' }}>
                          <DashboardEditControls
                            dashboardName={selectedDashboard?.name || ''}
                            onDashboardNameChange={onDashboardNameChange}
                            onSaveDashboard={() => {
                              dispatch({
                                type: 'saveCurrentDashboard',
                                dashboard: selectedDashboard,
                                sessionId,
                              });
                            }}
                            onAddWidget={addWidget}
                            activeWidgets={activeWidgets?.activeWidgets || []}
                          />
                        </div>
                        <div
                          style={{ gridArea: '1 / 1', visibility: 'hidden', pointerEvents: 'none' }}
                          aria-hidden
                        >
                          <DashboardViewControls
                            onMarkAsFavorite={() => setShowMarkAsFavoriteDashboardModal(true)}
                            dashboards={state.dashboards || []}
                            selectedDashboard={selectedDashboard}
                            onDelete={() => {
                              setShowDeleteDashboardModal(true);
                            }}
                            onEdit={() => {
                              dispatch({ type: 'editCurrentDashboard' });
                            }}
                            onDashboardCreate={() => setShowCreateDashboardModal(true)}
                            onDashboardShare={onDashboardShare}
                            onDashboardSelect={onDashboardSelect}
                          />
                        </div>
                      </div>
                    ) : (
                      <DashboardViewControls
                        onMarkAsFavorite={() => setShowMarkAsFavoriteDashboardModal(true)}
                        dashboards={state.dashboards || []}
                        selectedDashboard={selectedDashboard}
                        onDelete={() => {
                          setShowDeleteDashboardModal(true);
                        }}
                        onEdit={() => {
                          dispatch({ type: 'editCurrentDashboard' });
                        }}
                        onDashboardCreate={() => setShowCreateDashboardModal(true)}
                        onDashboardShare={onDashboardShare}
                        onDashboardSelect={onDashboardSelect}
                      />
                    )
                  ) : (
                    <button
                      className={'btn btn-outline-primary-500 btn-sm navbar-btn me-3'}
                      style={{ fontFamily: 'Inter' }}
                      onClick={() => setShowCreateDashboardModal(true)}
                    >
                      <FontAwesomeIcon icon={faTableColumns} style={{ fontSize: '1.15em' }} />{' '}
                      Create Dashboard
                    </button>
                  )}
                </div>
              </div>
            )}
          </ul>

          <ul className="navbar-nav mb-2 mb-lg-0 align-items-center">
            {/* User menu inside collapse on mobile only (hidden on sm+, shown via hamburger) */}
            <span className="d-sm-none">
              <DashboardUserMenu
                className={'ms-2'}
                notificationSoundOn={notificationSoundOn}
                setNotificationSoundOn={setNotificationSoundOn}
              />
            </span>
          </ul>
        </div>
      </div>
    </nav>
  );
}
